import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

import { useGetBanksQuery } from "web/features/pointsBank/pointsBankApiSlice";

import PointsLoadErrorBanner from "./pointsLoadErrorBanner";

interface IBannerSlotContext {
  register: () => () => void;
  hasPageHeader: boolean;
  isError: boolean;
  refetch: () => void;
}

const BannerSlotContext = createContext<IBannerSlotContext>({
  register: () => () => {},
  hasPageHeader: false,
  isError: false,
  refetch: () => {},
});

export const BannerSlotProvider: FC<PropsWithChildren> = ({ children }) => {
  const [pageHeadersCount, setPageHeadersCount] = useState(0);
  const { isError, refetch } = useGetBanksQuery();

  const register = useCallback(() => {
    setPageHeadersCount((count) => count + 1);
    return () => setPageHeadersCount((count) => count - 1);
  }, []);

  const value = useMemo(
    () => ({
      register,
      hasPageHeader: pageHeadersCount > 0,
      isError,
      refetch,
    }),
    [register, pageHeadersCount, isError, refetch]
  );

  return (
    <BannerSlotContext.Provider value={value}>
      {children}
    </BannerSlotContext.Provider>
  );
};

const usePageHeaderBanner = () => {
  const { register } = useContext(BannerSlotContext);

  useLayoutEffect(register, [register]);
};

export const GlobalPointsLoadErrorBanner: FC = () => {
  const { hasPageHeader, isError, refetch } = useContext(BannerSlotContext);

  return hasPageHeader ? null : (
    <PointsLoadErrorBanner isError={isError} refetch={refetch} />
  );
};

export const PageHeaderPointsLoadErrorBanner: FC = () => {
  usePageHeaderBanner();
  const { isError, refetch } = useContext(BannerSlotContext);

  return <PointsLoadErrorBanner isError={isError} refetch={refetch} />;
};
